/**
 * A query-builder component defines a step like query, consisting of
 * selection of events and exclusion steps
 */

import findKey from 'lodash/findKey';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import debounce from 'lodash/debounce';
import {Component} from 'panel';

import {dataGroupIDsEqual, serializeAttributes} from 'common/util';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {registerMPElement} from 'common/util/register-element';
import {findKeyByValue} from 'common/util/typescript';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import QueryEventsSection from 'common/widgets/query-events-section';
import {QueryEntry, FilterOperator, isQueryEventEntry} from 'common/widgets/query-event-selector/types';
import customEventsClient from 'common/report/queries/app-api/custom-events-client';
import {CustomEventDefinition} from 'common/types/reports/custom-events';

import {CAPTURED_TRACKING, TrackingEvent} from './tracking';

import template from './index.jade';
import css from './index.styl?inline';
import {queryIsInvalidForDataGroup, setQueryDataGroup} from 'common/widgets/query-builder/util/data-groups';
import {sanitizeGroupBys, sanitizeExclusionSteps} from 'common/widgets/query-builder/util';
import {FunnelAttribution, QueryBuilderSelection} from 'common/widgets/query-builder/types';
import {SectionItem} from 'common/components/items-menu/types';

type EventSourceName = `events` | `exclusion-steps`;

const COMPONENT_NAME = `mp-query-builder`;

export const ATTRIB_OPTIONS: SectionItem[] = [
  {
    label: `Last touch`,
    value: `last_touch`,
    isSelected: true,
  },
  {
    label: `First touch`,
    value: `first_touch`,
    isSelected: false,
  },
];

enum AttributeFields {
  attributionSelection = `breakdown-attribution-selection`,
  dataGroupId = `data-group-id`,
  dateRange = `date-range`,
  events = `events`,
  exclusionSteps = `exclusion-steps`,
  filters = `filters`,
  filtersOperator = `filters-operator`,
  funnelAttributionSelection = `funnel-breakdown-attribution`,
  groupBy = `group-by`,
}

const EVENT_SOURCE_MAP = Object.freeze({
  events: `events`,
  'exclusion-steps': `exclusionSteps`,
});

const ATTRIBUTE_READABLE_MAP = Object.freeze({
  events: `event`,
  'exclusion-steps': `exclusion step`,
  'group-by': `segment`,
});

interface QueryBuilderState {
  customEvents: CustomEventDefinition[];
  selectionObject: QueryBuilderSelection;
}

interface AttrSchema {
  'funnel-breakdown-attribution': FunnelAttribution;
  'enable-attribution': boolean;
  [key: string]: any;
}

export default registerMPElement(
  COMPONENT_NAME,
  class QueryBuilder extends Component<QueryBuilderState, void, void, AttrSchema> {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'add-entry-label': {
          type: `string`,
          default: ``,
          description: `Label to use on the main events section`,
        },
        'add-exclusion-event-label': {type: `string`, default: `Add exclusion step`},
        'breakdown-attribution': `json`,
        'breakdown-attribution-selection': `string`,
        'funnel-breakdown-attribution': `json`,
        'breakdown-resource-types': {
          type: `json`,
          default: [],
          description: `list of ResourceTypes to use in the breakdown menu`,
        },
        'data-group-id': `string`,
        'data-groups-enabled': `boolean`,
        'data-group-change-warning': `boolean`,
        'date-range': `json`,
        'disable-clone-event': {type: `boolean`, default: false},
        'disable-delete-event': {type: `boolean`, default: false},
        'enable-attribution': `boolean`,
        'enable-filters-operator': `boolean`,
        events: {
          type: `json`,
          default: [],
          description: `interface: QueryEntry[]`,
        },
        'events-row-label-type': {type: `string`, default: `numeric`},
        'exclude-autotrack-events': {
          type: `boolean`,
          default: false,
          description: `whether to hide autotrack events from the events menu`,
        },
        'enable-behavioral-properties': {type: `boolean`, default: false},
        'include-custom-properties': {
          type: `string`,
          enum: [`none`, `persisted-only`, `all`],
          default: `none`,
          description: `whether to hide custom properties from properties menus`,
        },
        'exclusion-steps': {
          type: `json`,
          default: [],
          description: `interface: QueryEntry[]`,
        },
        'filter-has-events': {type: `boolean`, default: true},
        'filter-has-users': {type: `boolean`, default: true},
        'filter-has-data-groups': {type: `boolean`, default: true},
        filters: {
          type: `json`,
          default: [],
          description: `interface: EventFilters`,
        },
        'filters-operator': {
          type: `string`,
          enum: Object.values(FilterOperator),
          default: FilterOperator.And,
        },
        'group-by': {
          type: `json`,
          default: [],
          description: `interface: Segment`,
        },
        'header-title': {
          type: `string`,
          default: `Steps`,
          description: `Section title for the main event selection`,
        },
        'max-data-history-days': {
          type: `number`,
          description: `max days time menu can query into data history`,
        },
        'metrics-mode': {
          type: `boolean`,
          description: `enables numeric property selection and aggregation functions`,
        },
        'multi-group-by': {
          type: `boolean`,
          description: `Allows multi segment selection`,
        },
        'multi-group-by-limitation': {
          type: `number`,
          default: null,
        },
        'project-has-people': `boolean`,
        'project-has-cohorts': `boolean`,
        'project-id': `number`,
        'row-limit': `number`,
        'row-types': QueryEventsSection.attrsSchema[`row-types`],
        'show-event-rename': `boolean`,
        'show-exclusion-steps': `boolean`,
        'hide-date-picker': {type: `boolean`, default: false},
        'hide-data-groups-selector': {type: `boolean`, default: false},
        'show-filter': {type: `boolean`, default: true},
        'show-group-by': {type: `boolean`, default: true},
        'show-any-event': `boolean`,
        'show-sessions': `boolean`,
        'show-special-date-property': `boolean`,
        'time-display-context': {type: `string`, default: DisplayContext.FullLength},
        'track-name': {
          type: `string`,
          description: `If specified, dispatches '[mp-query-builder] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
        },
        'track-properties': {
          type: `json`,
          default: {},
          description: `If specified, adds extra properties to the track event`,
        },
        'user-id': `number`,
        'support-b2b-cohorts': {
          type: `boolean`,
          description: `whether b2b cohorts are enabled`,
        },
        'support-inline-cohorts': {
          type: `boolean`,
          description: `indicates whether cohorts can be created/edited inline`,
        },
        'other-group-by-options': {type: `string`, default: `[]`},
        'events-draggable': {
          type: `boolean`,
          default: false,
          description: `enable drag-and-drop reordering on main events selection`,
        },
        'event-index-toggleable': {
          type: `boolean`,
          default: false,
          description: `if the row index label can be toggled`,
        },
        'event-index-toggle-label': `string`,
        'workspace-id': `number`,
        'query-type-options': {type: `json`, description: `ToggleOption[]`},
        'selected-query-type': `string`,
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        defaultState: {
          customEvents: [],
          selectionObject: null,
        },

        helpers: {
          serializeAttributes,
          BOOLEAN_OPERATORS: Object.values(FilterOperator),

          getPropertyMenuAttributes: (type) => this.getPropertyMenuAttributes(type),
          getDataGroupWarning: () => this.getDataGroupWarning(),
          hasSlot: (name) => this.hasSlot(name),
          hasEventsOnly: () => this.hasEventsOnly,
          hasUserEntriesOnly: () => this.hasUserEntriesOnly,
          onDataGroupSelected: ({detail: {dataGroup}}) => this.emitUpdateEvent({dataGroupId: dataGroup.id}),
          onFiltersOperatorSelected: (ev) => this.emitUpdateEvent({filtersOperator: ev.detail.value}),
          onSavedCustomEvent: () => this.emitUpdateEvent({}, {forceQuery: true}),
          onQueryTypeSelected: (ev) => this.emitUpdateEvent({queryType: ev.detail.selected}),
          preventTracking: (ev) => ev.stopPropagation(),
          propagateEvent: (ev) => propagateEvent(this, ev),
          updateFilters: ({detail: {items}}) => this.emitUpdateEvent({filters: items}),
          updateGroupBy: ({detail}) => this.emitUpdateEvent({groupBy: detail.items || detail.item}),
          updateTime: ({detail}) => this.emitUpdateEvent({dateRange: detail}),
          updateAttributionSelection: ({detail}) => {
            const props = {
              selected_breakdown_attribution_value: detail.item.value,
              selected_breakdown_attribution_type: detail.item.index,
            };
            this.track(TrackingEvent.SelectBreakdownAttribution, props);
            this.emitUpdateEvent({attributionSelection: detail.item.value});
          },
          updateFunnelAttributionSelection: ({detail}) => {
            const props = {
              selected_breakdown_attribution_value: detail.item.value,
            };
            this.track(TrackingEvent.SelectFunnelsBreakdownAttribution, props);
            let funnelAttributionSelection: FunnelAttribution = {action_mode: detail.item.value};
            if (!isNaN(detail.item.value)) {
              funnelAttributionSelection = {action_mode: `step`, action_step: detail.item.value};
            }
            this.emitUpdateEvent({funnelAttributionSelection});
          },
          /** Captures a track event to be tracked as part of this component */
          captureTracking: (ev) => {
            const {eventPrefix, props} = ev.detail;
            const eventName = ev.detail.eventName.replace(eventPrefix, ``);

            if (this.isCapturedTrackEvent(eventName)) {
              this.helpers.preventTracking(ev);
              this.track(eventName, props);
            }
          },

          /** Clears a given attribute by emitting a select event with its default value */
          clearSelection: (attr) => {
            const fieldName = findKeyByValue(AttributeFields, attr);
            const defaultValue = this.getDefaultAttribute(attr);

            this.emitUpdateEvent({[fieldName]: defaultValue});
            this.track(`Clear ${ATTRIBUTE_READABLE_MAP[attr]}`);
          },

          getEventEntries: (source): QueryEntry[] => this.getEventEntries(source),

          getEventValues: (source): string[] => {
            return this.getEventEntries(source)
              .filter(isQueryEventEntry)
              .map(({event}) => event.value);
          },

          getNumExclusionSteps: () => this.getEventEntries(`events`).length,

          /** Attribution related helpers */
          showAttributionSelector: () => {
            return (
              this.attr(`breakdown-attribution`) &&
              this.attr(`breakdown-attribution`).length > 1 &&
              this.attr(`group-by`) &&
              this.attr(`group-by`).length > 0
            );
          },
          getAttributionSelectedOption: () => {
            const items = this.attr(`breakdown-attribution`);
            for (const item of items) {
              if (item.value === this.attr(`breakdown-attribution-selection`)) {
                return item.label;
              }
            }
          },
          showFunnelAttributionSelector: () => {
            const hasBreakdowns = this.attr(`group-by`) && this.attr(`group-by`).length > 0 ? true : false;
            const hasFilters = this.attr(`filters`) && this.attr(`filters`).length > 0 ? true : false;
            return this.attr(`enable-attribution`) && (hasBreakdowns || hasFilters);
          },
          funnelAttributionItems: () => {
            const events = this.attr(`events`) || [];
            const stepItems = events.map((_, index) => ({
              label: `Step ${index + 1}`,
              value: index + 1,
              isSelected: false,
            }));
            const items = ATTRIB_OPTIONS.concat(stepItems);

            const current: FunnelAttribution = this.attr(`funnel-breakdown-attribution`);
            let selectedIndex = items
              .map((item) => item.value)
              .indexOf(current.action_mode === `step` ? current.action_step : current.action_mode);
            if (selectedIndex < 0) {
              selectedIndex = 0;
            }
            return [
              {
                items: items.map((item, index) => ({
                  ...item,
                  isSelected: index === selectedIndex,
                })),
              },
            ];
          },
          /** Attributes for mp-query-events-section metric analysis */
          getMetricsModeAttributes: () => {
            if (!this.attr(`metrics-mode`)) {
              return {};
            }

            return serializeAttributes({
              'row-label-type': `alpha`,
              'show-all-events': true,
              'show-top-events': true,
              'show-numeric-properties': true,
              'show-numeric-aggregations': true,
            });
          },

          /**
           * Determines if a current attribute differs from its default value
           */
          hasValue: (attr): boolean => !isEqual(this.getDefaultAttribute(attr), this.attr(attr)),

          updateEventSection: (ev, source) => {
            const eventEntries: QueryEntry[] = ev.detail.eventEntries;
            this.updateEventEntries(eventEntries, source);
          },
        },
      };
    }

    get hasEventsOnly() {
      return this.state.selectionObject[`events`].every(
        (entry) => (entry.type || ResourceType.Event) === ResourceType.Event,
      );
    }

    get hasUserEntriesOnly() {
      return this.state.selectionObject[`events`].every((entry) => entry.type === ResourceType.User);
    }

    private dispatchUpdate: Function;

    constructor() {
      super();
      // we use debounce such that even if we have multiple updates within a frame, we send the last
      // one up, to not spam network queries.
      this.dispatchUpdate = debounce((ev) => this._dispatchUpdate(ev), 100);
      this.update({
        selectionObject: this.getSelectionObjectFromAttrs(),
      });
    }

    public attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);

      if (Object.values(AttributeFields).includes(attr)) {
        /**
         * a single object representing the current
         * selection state of all passed attributes
         */

        // Whenever we get new data attributes, we buffer them to this.selectionObject. This is
        // because within a frame, we might have multiple update events, from multiple components
        // handling callbacks underneath. Since the frame hasn't finished, the attributes are not
        // updated yet, so reading from them would give us a outdated view of the world. Instead, we
        // use selectionObject to buffer any changes to the state within a frame, and reset it when
        // we come to the next frame (when attributes are changed)
        this.update({selectionObject: this.getSelectionObjectFromAttrs()});
      } else if (attr === `project-id` && this.initialized) {
        this.fetchCustomEvents();
      } else if (attr === `workspace-id` && this.initialized) {
        this.fetchCustomEvents();
      }
    }

    public connectedCallback() {
      super.connectedCallback();

      this.update({
        selectionObject: this.getSelectionObjectFromAttrs(),
      });

      this.fetchCustomEvents();
    }

    private getSelectionObjectFromAttrs(): QueryBuilderSelection {
      return mapValues(AttributeFields, (attribute) => this.attr(attribute));
    }

    /**
     * Checks for presence of a slotted child element
     */
    private hasSlot(slotName: string): boolean {
      return !!this.querySelector(`[slot="${slotName}"]`);
    }

    /**
     * Whether or not to show a warning tooltip when switching data groups
     */
    private getDataGroupWarning(): boolean {
      // TODO: update how change warning is implemented so we can specify which data groups are invalid to switch to
      // The 'non-user data group' string is a hack to detect if the query is valid for an arbitrary non-null data group
      return (
        !this.state.selectionObject.dataGroupId &&
        queryIsInvalidForDataGroup({
          dataGroupId: `non-user data group`,
          query: this.state.selectionObject,
          customEvents: this.state.customEvents,
        })
      );
    }

    /**
     * Returns the default value from attrsSchema for a
     * given attribute, throws an error where no default
     * value is explicitly defined
     */
    private getDefaultAttribute(attrName: string) {
      const attribute = QueryBuilder.attrsSchema[attrName];

      if (!attribute.hasOwnProperty(`default`)) {
        throw new Error(`No default value found for ${attrName}`);
      }

      return attribute.default;
    }

    private getPropertyMenuAttributes(type: string) {
      const groupsEnabled = this.attr(`data-groups-enabled`);

      const attrs = {
        'include-custom-properties': this.attr(`include-custom-properties`),
        'track-name': this.attr(`track-name`),
      };

      let otherAttrs;
      if (this.hasEventsOnly) {
        const types = this.attr(`breakdown-resource-types`);
        if (types.length && type === `breakdown`) {
          otherAttrs = {
            'all-data-groups': false,
            'project-has-cohorts': types.includes(ResourceType.Cohort),
            'project-has-people': types.includes(ResourceType.User),
            'project-has-events': types.includes(ResourceType.Event),
          };
        } else {
          otherAttrs = {
            'all-data-groups': groupsEnabled,
            'project-has-cohorts': this.attr(`project-has-cohorts`),
            'project-has-people': this.attr(`project-has-people`),
            'project-has-events': true,
          };
        }
      } else {
        otherAttrs = {
          'all-data-groups': groupsEnabled && this.hasUserEntriesOnly,
          'project-has-cohorts': this.attr(`project-has-cohorts`),
          'project-has-people': this.attr(`project-has-people`) && this.hasUserEntriesOnly,
          'project-has-events': false,
        };
      }

      return {...attrs, ...otherAttrs};
    }

    /**
     * Returns a QueryEntry collection
     */
    private getEventEntries(source: EventSourceName): QueryEntry[] {
      return this.attr(source).map((entry) => ({
        ...entry,
        type: entry.type || ResourceType.Event,
      }));
    }

    private updateEventEntries(events: QueryEntry[], source: EventSourceName) {
      this.emitUpdateEvent({[EVENT_SOURCE_MAP[source]]: events});
    }

    private emitUpdateEvent(updates: Partial<QueryBuilderSelection>, {forceQuery = false} = {}) {
      const existingSelection = this.state.selectionObject;
      let selection = {...existingSelection, ...updates};
      selection = {
        ...selection,
        exclusionSteps: sanitizeExclusionSteps(selection.exclusionSteps, selection.events),
        groupBy: sanitizeGroupBys(selection.events, selection.groupBy),
      };
      if (
        updates.hasOwnProperty(`dataGroupId`) &&
        !dataGroupIDsEqual(updates.dataGroupId, existingSelection.dataGroupId)
      ) {
        selection = setQueryDataGroup({
          query: selection,
          dataGroupId: updates.dataGroupId,
          customEvents: this.state.customEvents,
        });
      }
      const detail = {
        ...selection,
        forceQuery,
        delta: updates,
      };
      this.update({selectionObject: selection});

      this.dispatchUpdate(new CustomEvent(`update`, {detail}));
    }

    private _dispatchUpdate(ev) {
      this.dispatchEvent(ev);
    }

    /**
     * Emits track events that bubble up
     */
    private track(event: string, props: object = {}) {
      if (!this.attr(`track-name`)) {
        return;
      }

      if (this.isCapturedTrackEvent(event)) {
        emitTrackEvent(this, {
          eventName: event,
          eventPrefix: COMPONENT_NAME,
          props: {
            ...props,
            project_id: this.attr(`project-id`),
            tab: this.attr(`track-name`),
            source: `query-builder`,
          },
          cancelable: true,
        });
      } else {
        throw new Error(`tracking event not defined: ${event}`);
      }
    }

    private isCapturedTrackEvent(event: string) {
      return findKey(TrackingEvent, (eventName) => eventName === event) || CAPTURED_TRACKING.has(event);
    }

    private async fetchCustomEvents() {
      const params = {
        projectID: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      };
      if (!params.projectID && !params.workspaceId) {
        // This check is necessary because retention adds mp-query-builder to the dom before setting attributes, so
        // they are blank when this method is called in connectedCallback
        return;
      }

      const customEvents = await customEventsClient.query(params);

      const paramsAreEqual =
        params.projectID === this.attr(`project-id`) && params.workspaceId === this.attr(`workspace-id`);
      if (paramsAreEqual) {
        this.update({customEvents});
      }
    }
  },
);
